import { useState, useMemo, useEffect } from "react"
import {
  OIL_DATABASE,
  type Unit,
  type LiquidType,
  type LyeType,
  type LyeCalculationMode,
  type OilEntry,
  type SoapCalculations,
} from "@/data/oilDatabase"
import type { SoapRecipe, RecipeOil } from "@/hooks/useRecipes"
import toast from "react-hot-toast"

export function useSoapCalculator(initialRecipe?: SoapRecipe | null) {
  const [unit, setUnit] = useState<Unit>("grams")
  const [liquidType, setLiquidType] = useState<LiquidType>("water")
  const [lyeType, setLyeType] = useState<LyeType>("NaOH")
  const [superfat, setSuperfat] = useState(5)
  const [waterPercent, setWaterPercent] = useState(33)
  const [lyeCalculationMode, setLyeCalculationMode] = useState<LyeCalculationMode>("ratio")
  const [lyeConcentration, setLyeConcentration] = useState(33)
  const [batchSize, setBatchSize] = useState(1000)
  const [fragrancePercent, setFragrancePercent] = useState(3)
  const [oils, setOils] = useState<OilEntry[]>([])
  const [loadedRecipeId, setLoadedRecipeId] = useState<string | null>(null)
  const [loadedRecipeName, setLoadedRecipeName] = useState<string>("")

  // Load recipe from navigation state (coming from SavedRecipes page)
  useEffect(() => {
    if (!initialRecipe) return
    const recipe = initialRecipe
    setUnit(recipe.unit as Unit)
    setLiquidType(recipe.liquidType as LiquidType)
    setLyeType(recipe.lyeType as LyeType)
    setSuperfat(recipe.superfat)
    setWaterPercent(recipe.waterPercent)
    setLyeCalculationMode(recipe.lyeCalculationMode as LyeCalculationMode)
    setLyeConcentration(recipe.lyeConcentration)
    setBatchSize(recipe.batchSize)
    setFragrancePercent(recipe.fragrancePercent)
    setOils(
      recipe.oils.map((o: RecipeOil, i: number) => ({
        id: `loaded-${i}-${Date.now()}`,
        oil: o.oil,
        percentage: o.percentage,
        weight: 0,
        costPerUnit: o.costPerUnit || 0,
      }))
    )
    setLoadedRecipeId(recipe._id || null)
    setLoadedRecipeName(recipe.name)
    toast.success(`Loaded recipe: ${recipe.name}`)
    window.history.replaceState({}, document.title)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addOil = (oilName: string) => {
    setOils((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        oil: oilName,
        percentage: 0,
        weight: 0,
        costPerUnit: 0,
      },
    ])
  }

  const removeOil = (id: string) => {
    setOils((prev) => prev.filter((o) => o.id !== id))
  }

  const updateOil = (id: string, field: keyof OilEntry, value: number | string) => {
    setOils((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)))
  }

  const balanceToHundred = () => {
    setOils((prev) => {
      if (prev.length === 0) return prev
      const total = prev.reduce((sum, oil) => sum + oil.percentage, 0)
      if (total === 0) {
        const evenSplit = 100 / prev.length
        return prev.map((oil) => ({ ...oil, percentage: evenSplit }))
      }
      return prev.map((oil) => ({ ...oil, percentage: (oil.percentage / total) * 100 }))
    })
  }

  const totalPercentage = useMemo(
    () => oils.reduce((sum, oil) => sum + oil.percentage, 0),
    [oils]
  )

  const calculations: SoapCalculations = useMemo(() => {
    const oilsWithCalculatedWeights = oils.map((oil) => ({
      ...oil,
      weight: (oil.percentage / 100) * batchSize,
    }))

    let totalLyeRequired = 0
    oilsWithCalculatedWeights.forEach((entry) => {
      const oilData = OIL_DATABASE.find((o) => o.name === entry.oil)
      if (oilData) {
        const sapValue = lyeType === "NaOH" ? oilData.sapNaOH : oilData.sapKOH
        totalLyeRequired += entry.weight * sapValue
      }
    })

    // Apply superfat discount
    totalLyeRequired = totalLyeRequired * (1 - superfat / 100)

    // Calculate liquid based on mode
    let totalLiquid = 0
    if (lyeCalculationMode === "ratio") {
      totalLiquid = (batchSize * waterPercent) / 100
    } else {
      totalLiquid = totalLyeRequired / (lyeConcentration / 100) - totalLyeRequired
    }

    const fragranceLoad = batchSize * (fragrancePercent / 100)
    const totalBatchWeight = batchSize + totalLyeRequired + totalLiquid + fragranceLoad

    // Calculate quality metrics
    let hardness = 0,
      cleansing = 0,
      conditioning = 0,
      bubbly = 0,
      creamy = 0
    oilsWithCalculatedWeights.forEach((entry) => {
      const oilData = OIL_DATABASE.find((o) => o.name === entry.oil)
      if (oilData && totalPercentage > 0) {
        const factor = entry.percentage / 100
        hardness += oilData.hardness * factor
        cleansing += oilData.cleansing * factor
        conditioning += oilData.conditioning * factor
        bubbly += oilData.bubbly * factor
        creamy += oilData.creamy * factor
      }
    })

    const totalCost = oilsWithCalculatedWeights.reduce((sum, oil) => {
      return sum + oil.weight * oil.costPerUnit
    }, 0)

    return {
      totalOilWeight: batchSize,
      totalLyeRequired,
      totalLiquid,
      fragranceLoad,
      totalBatchWeight,
      totalPercentage,
      hardness,
      cleansing,
      conditioning,
      bubbly,
      creamy,
      totalCost,
      oilsWithCalculatedWeights,
    }
  }, [oils, lyeType, superfat, waterPercent, lyeConcentration, lyeCalculationMode, batchSize, fragrancePercent, totalPercentage])

  return {
    // Recipe settings
    unit, setUnit,
    liquidType, setLiquidType,
    lyeType, setLyeType,
    superfat, setSuperfat,
    waterPercent, setWaterPercent,
    lyeCalculationMode, setLyeCalculationMode,
    lyeConcentration, setLyeConcentration,
    batchSize, setBatchSize,
    fragrancePercent, setFragrancePercent,

    // Oil management
    oils,
    addOil,
    removeOil,
    updateOil,
    balanceToHundred,

    // Loaded recipe
    loadedRecipeId,
    loadedRecipeName,
    setLoadedRecipe: (id: string | null, name: string) => {
      setLoadedRecipeId(id)
      setLoadedRecipeName(name)
    },

    // Calculations
    calculations,
    totalPercentage,
  }
}
